import { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    FormLabel,
    RadioGroup,
    Radio,
    OutlinedInput,
} from "@mui/material";
import { Alert, Image } from "antd";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { TimePicker } from "antd";
import useApiRequest from "utils/Apirequest";
import { useMaterialUIController } from "context";
import axios from "axios";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

const triggerMessageOptions = [
    "BootNotification",
    "StatusNotification",
    "Heartbeat",
    "MeterValues",
    "DiagnosticsStatusNotification",
    "FirmwareStatusNotification",
];

function ActionModal({
    open,
    onClose,
    onSubmit,
    chargerInfo,
    specificConnector,
    configData,
}) {
    const [selectedAction, setSelectedAction] = useState("");
    const [timeInterval, setTimeInterval] = useState(null);
    const [meterConfigOption, setMeterConfigOption] = useState("");

    const [stopTxnOnDisconnect, setStopTxnOnDisconnect] = useState(false);
    const [authorizeRemoteTxRequests, setAuthorizeRemoteTxRequests] =
        useState(false);
    const [connectionTimeOut, setConnectionTimeOut] = useState(null);
    const [
        unlockConnectorOnEVSideDisconnect,
        setUnlockConnectorOnEVSideDisconnect,
    ] = useState(false);
    const [localAuthListEnabled, setLocalAuthListEnabled] = useState(false);
    const [localPreAuthorize, setLocalPreAuthorize] = useState(false);
    const [allowOfflineTxForUnknownId, setAllowOfflineTxForUnknownId] =
        useState(false);
    const [stopTransactionOnInvalidId, setStopTransactionOnInvalidId] =
        useState(false);
    const [webSocketPingInterval, setWebSocketPingInterval] = useState(null);
    const [diagnosticsUploadInterval, setDiagnosticsUploadInterval] =
        useState(null);
    const [CentralSystemURL, setCentralSystemURL] = useState("");

    const [heartbeatInterval, setHeartbeatInterval] = useState(null);
    const [reason, setReason] = useState("");
    const [triggerMessage, setTriggerMessage] = useState("");
    const [controller] = useMaterialUIController();
    const { sidenavColor } = controller;
    const [tagId, setTagId] = useState("");
    const [vehicleId, setVehicleId] = useState("");
    const [startSessionReason, setStartSessionReason] = useState("");
    const [vehicles, setVehicles] = useState([]);
    const [sessionId, setSessionId] = useState("");
    const [stopSessionReason, setStopSessionReason] = useState("");

    const [availability, setAvailability] = useState("operative");

    const handleAvailabilityChange = (event) => {
        setAvailability(event.target.value);
    };

    const handleVehicleChange = (event) => {
        setVehicleId(event.target.value);
    };

    const actionOptions = [
        "Soft Reset",
        "Hard Reset",
        "Add/Update Configuration",
        "Trigger Message",
        "Change Availability",
        "Start Session",
        "Stop Session",
    ];

    const meterConfigOptions = useMemo(() => {
        const baseOptions = [
            "Meter Value Interval",
            "Stop Transaction On EV Side Disconnect",
            "Heartbeat Interval",
            "AuthorizeRemoteTxRequests",
            "ConnectionTimeOut",
            "UnlockConnectorOnEVSideDisconnect",
            "LocalAuthListEnabled",
            "LocalPreAuthorize",
            "AllowOfflineTxForUnknownId",
            "StopTransactionOnInvalidId",
            "WebSocketPingInterval",
            "StopTxnSampledData",
            "DiagnosticsUploadInterval",
            "FirmwareUpdateURL",
            "SupportedFeatureProfiles",
        ];

        if (!Array.isArray(configData)) {
            return baseOptions;
        }

        const hasCentralSystemURL = configData.find(
            (item) => item.key === "OCPPEndpointToBackend" && item.readonly === false
        );

        if (hasCentralSystemURL) {
            baseOptions.push("CentralSystemURL");
        }

        return baseOptions;
    }, [configData]);

    const handleClose = () => {
        onClose();
        setSelectedAction("");
        setMeterConfigOption("");
        setTimeInterval(null);
        setStopTxnOnDisconnect(false);
        setHeartbeatInterval(null);
        setReason("");
        setTriggerMessage("");
    };

    const apiRequest = useApiRequest();

    const getAllVehiclesOfUser = () => {
        axios({
            method: "get",
            url: process.env.REACT_APP_BASEURL + `users/user/${tagId}/vehicles`,
            withCredentials: true,

        })
            .then((response) => {
                if (response?.data?.success === true) {
                    const data = response?.data?.data;

                    setVehicles(data);
                } else {
                    setVehicles([]);
                }
            })
            .catch((error) => {
                setVehicles([]);
            });
    };

    useEffect(() => {
        if (tagId) {
            getAllVehiclesOfUser();
        } else {
            setVehicles([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tagId]);

    const isFormValid = () => {
        if (!selectedAction) {
            return false;
        }

        switch (selectedAction) {
            case "Add/Update Configuration":
                if (!meterConfigOption) return false;
                if (meterConfigOption === "Meter Value Interval") return !!timeInterval;
                if (meterConfigOption === "Heartbeat Interval")
                    return !!heartbeatInterval;
                if (meterConfigOption === "Stop Transaction On EV Side Disconnect")
                    return true;
                if (meterConfigOption === "AuthorizeRemoteTxRequests") return true;
                if (meterConfigOption === "UnlockConnectorOnEVSideDisconnect")
                    return true;
                if (meterConfigOption === "LocalAuthListEnabled") return true;
                if (meterConfigOption === "LocalPreAuthorize") return true;
                if (meterConfigOption === "AllowOfflineTxForUnknownId") return true;
                if (meterConfigOption === "StopTransactionOnInvalidId") return true;
                if (meterConfigOption === "ConnectionTimeOut")
                    return !!connectionTimeOut;
                if (meterConfigOption === "WebSocketPingInterval")
                    return !!webSocketPingInterval;
                if (meterConfigOption === "DiagnosticsUploadInterval")
                    return !!diagnosticsUploadInterval;
                if (meterConfigOption === "CentralSystemURL") return !!CentralSystemURL;

                return false;

            case "Soft Reset":
            case "Hard Reset":
                return reason.trim() !== "";

            case "Trigger Message":
                return !!triggerMessage;
            case "Change Availability":
                return !!availability;
            case "Start Session":
                return !!tagId && !!vehicleId && !!startSessionReason;
            case "Stop Session":
                return !!sessionId && !!stopSessionReason;

            default:
                return false;
        }
    };

    const handleSubmit = async () => {
        // eslint-disable-next-line default-case
        switch (selectedAction) {
            case "Add/Update Configuration":
                if (meterConfigOption === "Meter Value Interval" && timeInterval) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "MeterValueSampleInterval",
                            value: timeInterval.minute() * 60 + timeInterval.second(),
                        },
                    };

                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "Stop Transaction On EV Side Disconnect"
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "StopTransactionOnEVSideDisconnect",
                            value: stopTxnOnDisconnect,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "Heartbeat Interval" &&
                    heartbeatInterval
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "HeartbeatInterval",
                            value:
                                heartbeatInterval.minute() * 60 + heartbeatInterval.second(),
                        },
                    };

                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "AuthorizeRemoteTxRequests" &&
                    authorizeRemoteTxRequests
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "AuthorizeRemoteTxRequests",
                            value: authorizeRemoteTxRequests,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "ConnectionTimeOut" &&
                    connectionTimeOut
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "ConnectionTimeOut",
                            value:
                                connectionTimeOut.minute() * 60 + connectionTimeOut.second(),
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "UnlockConnectorOnEVSideDisconnect" &&
                    unlockConnectorOnEVSideDisconnect
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "UnlockConnectorOnEVSideDisconnect",
                            value: unlockConnectorOnEVSideDisconnect,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "LocalAuthListEnabled" &&
                    localAuthListEnabled
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "LocalAuthListEnabled",
                            value: localAuthListEnabled,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "LocalPreAuthorize" &&
                    localPreAuthorize
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "LocalPreAuthorize",
                            value: localPreAuthorize,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "AllowOfflineTxForUnknownId" &&
                    allowOfflineTxForUnknownId
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "AllowOfflineTxForUnknownId",
                            value: allowOfflineTxForUnknownId,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "StopTransactionOnInvalidId" &&
                    stopTransactionOnInvalidId
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "StopTransactionOnInvalidId",
                            value: stopTransactionOnInvalidId,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "WebSocketPingInterval" &&
                    webSocketPingInterval
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "WebSocketPingInterval",
                            value:
                                webSocketPingInterval.minute() * 60 +
                                webSocketPingInterval.second(),
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                } else if (
                    meterConfigOption === "CentralSystemURL" &&
                    CentralSystemURL
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "OCPPEndpointToBackend",
                            value: CentralSystemURL,
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    } else {
                    }
                } else if (
                    meterConfigOption === "DiagnosticsUploadInterval" &&
                    diagnosticsUploadInterval
                ) {
                    const payload = {
                        chargerId: chargerInfo?.name,
                        action: "ChangeConfiguration",
                        payload: {
                            key: "DiagnosticsUploadInterval",
                            value:
                                diagnosticsUploadInterval.minute() * 60 +
                                diagnosticsUploadInterval.second(),
                        },
                    };
                    const url = "session/charger-command";
                    const { data } =
                        (await apiRequest(
                            "post",
                            process.env.REACT_APP_BASEURL + url,
                            payload,
                            {},
                            
                        )) || {};
                    if (data?.success) {
                    }
                }
                break;
            case "Soft Reset":
            case "Hard Reset":
                const type = selectedAction === "Soft Reset" ? "Soft" : "Hard";

                const payload = {
                    chargerId: chargerInfo?.name,
                    type: type,
                    resetReason: reason,
                };
                const url = "session/reset";
                const { data } =
                    (await apiRequest(
                        "post",
                        process.env.REACT_APP_BASEURL + url,
                        payload,
                        {},
                        
                    )) || {};
                if (data?.success) {
                }
                break;
            case "Trigger Message":
                const payload1 = {
                    requestedMessage: triggerMessage,
                    chargerId: chargerInfo?.name,
                    connectorId: specificConnector?.connectorId,
                };
                const url1 = "session/trigger-charger";
                const { data1 } =
                    (await apiRequest(
                        "post",
                        process.env.REACT_APP_BASEURL + url1,
                        payload1,
                        {},
                        
                    )) || {};
                if (data1?.success) {
                }
                break;
            case "Change Availability":
                const payload2 = {
                    chargerId: chargerInfo?.name,
                    action: "ChangeAvailability",
                    payload: {
                        connectorId: specificConnector?.connectorId,
                        type: availability,
                    },
                };
                const url2 = "session/charger-command";
                const { data2 } =
                    (await apiRequest(
                        "post",
                        process.env.REACT_APP_BASEURL + url2,
                        payload2,
                        {},
                        
                    )) || {};
                if (data2?.success) {
                } else {
                }
                break;

            case "Start Session":
                const payload3 = {
                    action: "start",
                    chargerId: chargerInfo?.name,
                    vehicleId: vehicleId || null,
                    payload: {
                        idTag: tagId,
                        connectorId: specificConnector?.connectorId,
                    },
                    reason: startSessionReason || "NA",
                };
                const url3 = "session/transaction";
                const { data3 } =
                    (await apiRequest(
                        "post",
                        process.env.REACT_APP_BASEURL + url3,
                        payload3,
                        {},
                        {
                            "Content-Type": "application/json",
                        }
                    )) || {};
                if (data3?.success) {
                }
                break;
            case "Stop Session":
                const payload4 = {
                    action: "stop",
                    chargerId: chargerInfo?.name,
                    payload: {
                        sessionId: sessionId,
                    },
                    sessionReason: stopSessionReason || "NA",
                };
                const url4 = "session/transaction";
                const { data4 } =
                    (await apiRequest(
                        "post",
                        process.env.REACT_APP_BASEURL + url4,
                        payload4,
                        {},
                        {
                            "Content-Type": "application/json",
                          
                        }
                    )) || {};
                if (data4?.success) {
                }
                break;
            default:

                break;
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
        handleClose();
        window.location.reload();
    };

    const renderMeterConfigInput = () => {
        switch (meterConfigOption) {
            case "Meter Value Interval":
                return (
                    <TimePicker
                        value={timeInterval}
                        onChange={(time) => setTimeInterval(time)}
                        format="mm:ss"
                        placeholder="Interval (mm:ss)"
                        style={{ width: "100%", marginTop: "16px" }}
                    />
                );
            case "Stop Transaction On EV Side Disconnect":
                return (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={stopTxnOnDisconnect}
                                    onChange={(e) => setStopTxnOnDisconnect(e.target.checked)}
                                />
                            }
                            label={stopTxnOnDisconnect ? "True" : "False"}
                            sx={{ mt: 2, display: "block" }}
                        />
                        <Alert
                            message={
                                <span>
                                    When set to true, the Charge Point SHALL administratively stop
                                    the transaction when the cable is unplugged from the EV.
                                </span>
                            }
                            type="info"
                            showIcon
                        />
                    </>
                );
            case "Heartbeat Interval":
                return (
                    <TimePicker
                        value={heartbeatInterval}
                        onChange={(time) => setHeartbeatInterval(time)}
                        format="mm:ss"
                        placeholder="Interval (mm:ss)"
                        style={{ width: "100%", marginTop: "16px" }}
                    />
                );
            case "AuthorizeRemoteTxRequests":
                return (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={authorizeRemoteTxRequests}
                                    onChange={(e) =>
                                        setAuthorizeRemoteTxRequests(e.target.checked)
                                    }
                                />
                            }
                            label={authorizeRemoteTxRequests ? "True" : "False"}
                            sx={{ mt: 2, display: "block" }}
                        />
                    </>
                );
            case "ConnectionTimeOut":
                return (
                    <TimePicker
                        value={connectionTimeOut}
                        onChange={(time) => setConnectionTimeOut(time)}
                        format="mm:ss"
                        placeholder="Interval (mm:ss)"
                        style={{ width: "100%", marginTop: "16px" }}
                    />
                );
            case "CentralSystemURL":
                return (
                    <TextField
                        label="CentralSystemURL"
                        value={CentralSystemURL}
                        onChange={(e) => setCentralSystemURL(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ mt: 3 }}
                    />
                );
            case "UnlockConnectorOnEVSideDisconnect":
                return (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={unlockConnectorOnEVSideDisconnect}
                                    onChange={(e) =>
                                        setUnlockConnectorOnEVSideDisconnect(e.target.checked)
                                    }
                                />
                            }
                            label={unlockConnectorOnEVSideDisconnect ? "True" : "False"}
                            sx={{ mt: 2, display: "block" }}
                        />
                    </>
                );
            case "LocalAuthListEnabled":
                return (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={localAuthListEnabled}
                                    onChange={(e) => setLocalAuthListEnabled(e.target.checked)}
                                />
                            }
                            label={localAuthListEnabled ? "True" : "False"}
                            sx={{ mt: 2, display: "block" }}
                        />
                    </>
                );
            case "LocalPreAuthorize":
                return (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={localPreAuthorize}
                                    onChange={(e) => setLocalPreAuthorize(e.target.checked)}
                                />
                            }
                            label={localPreAuthorize ? "True" : "False"}
                            sx={{ mt: 2, display: "block" }}
                        />
                    </>
                );
            case "AllowOfflineTxForUnknownId":
                return (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={allowOfflineTxForUnknownId}
                                    onChange={(e) =>
                                        setAllowOfflineTxForUnknownId(e.target.checked)
                                    }
                                />
                            }
                            label={allowOfflineTxForUnknownId ? "True" : "False"}
                            sx={{ mt: 2, display: "block" }}
                        />
                    </>
                );
            case "StopTransactionOnInvalidId":
                return (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={stopTransactionOnInvalidId}
                                    onChange={(e) =>
                                        setStopTransactionOnInvalidId(e.target.checked)
                                    }
                                />
                            }
                            label={stopTransactionOnInvalidId ? "True" : "False"}
                            sx={{ mt: 2, display: "block" }}
                        />
                    </>
                );
            case "WebSocketPingInterval":
                return (
                    <TimePicker
                        value={webSocketPingInterval}
                        onChange={(time) => setWebSocketPingInterval(time)}
                        format="mm:ss"
                        placeholder="Interval (mm:ss)"
                        style={{ width: "100%", marginTop: "16px" }}
                    />
                );
            case "DiagnosticsUploadInterval":
                return (
                    <TimePicker
                        value={diagnosticsUploadInterval}
                        onChange={(time) => setDiagnosticsUploadInterval(time)}
                        format="mm:ss"
                        placeholder="Interval (mm:ss)"
                        style={{ width: "100%", marginTop: "16px" }}
                    />
                );
            default:
                return null;
        }
    };
    const renderConditionalFields = () => {
        switch (selectedAction) {
            case "Add/Update Configuration":
                return (
                    <>
                        <FormControl fullWidth sx={{ mt: 3 }}>
                            <InputLabel>Config Type</InputLabel>
                            <Select
                                value={meterConfigOption}
                                label="Config Type"
                                onChange={(e) => setMeterConfigOption(e.target.value)}
                                sx={{ height: "50px" }}
                            >
                                {meterConfigOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderMeterConfigInput()}
                    </>
                );
            case "Soft Reset":
            case "Hard Reset":
                return (
                    <TextField
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        sx={{ mt: 3 }}
                    />
                );
            case "Trigger Message":
                return (
                    <FormControl fullWidth sx={{ mt: 3 }}>
                        <InputLabel>Message Type</InputLabel>
                        <Select
                            value={triggerMessage}
                            label="Message Type"
                            onChange={(e) => setTriggerMessage(e.target.value)}
                            sx={{ height: "50px" }}
                        >
                            {triggerMessageOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            case "Change Availability":
                return (
                    <>
                        <FormControl component="fieldset" sx={{ mt: 2 }}>
                            <FormLabel component="legend">Status</FormLabel>
                            <RadioGroup
                                row
                                aria-label="availability"
                                name="availability-radio-buttons"
                                value={availability}
                                onChange={handleAvailabilityChange}
                            >
                                <FormControlLabel
                                    value="operative"
                                    control={<Radio />}
                                    label="Operative"
                                />
                                <FormControlLabel
                                    value="inoperative"
                                    control={<Radio />}
                                    label="Inoperative"
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                );
            case "Start Session":
                return (
                    <MDBox p={0} pt={1} component="form" role="form">
                        <MDBox p={0}>
                            <MDInput
                                type="text"
                                label="Mobile number / Tag ID"
                                placeholder="Enter mobile number / Tag ID"
                                value={tagId}
                                name="tag_id"
                                margin="dense"
                                fullWidth={true}
                                onChange={(e) => setTagId(e.target.value)}
                            />
                        </MDBox>

                        <MDBox p={0} pt={1}>
                            <FormControl fullWidth>
                                <InputLabel id="demo-multiple-name-label">Vehicle</InputLabel>
                                <Select
                                    showSearch
                                    sx={{
                                        height: 50,
                                    }}
                                    labelId="demo-multiple-name-label"
                                    id="demo-multiple-name"
                                    placeholder="Select Vehicle"
                                    value={vehicleId}
                                    name="vehicleId"
                                    onChange={handleVehicleChange}
                                    input={<OutlinedInput label="Vehicle" />}
                                >
                                    {vehicles.length > 0 ? (
                                        vehicles.map((city, index) => (
                                            <MenuItem key={index} value={city._id}>
                                                {city.vehicle_img && (
                                                    <Image
                                                        src={
                                                            process.env.REACT_APP_PUBLIC_URL +
                                                            city.vehicle_img
                                                        }
                                                        width={24}
                                                        height={24}
                                                        preview={false}
                                                        alt="logo"
                                                    />
                                                )}
                                                {city.make} {city.model} {city.variant}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>No Data</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </MDBox>

                        <MDBox p={0} pt={1}>
                            <MDInput
                                type="text"
                                label="Start reason"
                                value={startSessionReason}
                                name="reason"
                                multiline
                                rows={5}
                                placeholder="Enter Reason for Starting the Charger.. "
                                margin="dense"
                                fullWidth={true}
                                onChange={(e) => setStartSessionReason(e.target.value)}
                            />
                        </MDBox>
                    </MDBox>
                );
            case "Stop Session":
                return (
                    <MDBox p={0} pt={1} component="form" role="form">
                        <MDBox p={0}>
                            <MDInput
                                type="text"
                                label="Session ID"
                                value={sessionId}
                                name="session_id"
                                margin="dense"
                                fullWidth={true}
                                onChange={(e) => setSessionId(e.target.value)}
                            />
                        </MDBox>
                        <MDBox p={0} pt={1}>
                            <MDInput
                                type="text"
                                label="Stop reason"
                                value={stopSessionReason}
                                name="reason"
                                multiline
                                rows={5}
                                placeholder="Enter stop reason"
                                margin="dense"
                                fullWidth={true}
                                onChange={(e) => setStopSessionReason(e.target.value)}
                            />
                        </MDBox>
                    </MDBox>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>
                <MDTypography variant="h5" fontWeight="medium">
                    Select OCPP Action
                </MDTypography>
            </DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="select-action-label">Action</InputLabel>
                    <Select
                        labelId="select-action-label"
                        value={selectedAction}
                        label="Action"
                        onChange={(e) => setSelectedAction(e.target.value)}
                        sx={{ height: "50px" }}
                    >
                        {actionOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Render the conditional field here */}
                {renderConditionalFields()}
            </DialogContent>
            <DialogActions>
                <MDButton variant="text" color="secondary" onClick={handleClose}>
                    Cancel
                </MDButton>
                <MDButton
                    variant="gradient"
                    color={sidenavColor}
                    onClick={handleSubmit}
                    disabled={!isFormValid()}
                >
                    Send Command
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

export default ActionModal;
